import React from "react";
import PropTypes from "prop-types";
import isFunction from "lodash-es/isFunction";

import { isDirty } from "lib/form";
import { getDisplayName } from "lib/react";
import ValidationError from "errors/Validation";

const props = {
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    hasFiles: PropTypes.bool,
    submit: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    refForm: PropTypes.func,
    onChange: PropTypes.func,
    target: PropTypes.string,
    action: PropTypes.string,
};

const form = (WrappedComponent) => {
    class Form extends React.Component {
        constructor(props) {
            super(props);

            this.onSubmit = this.onSubmit.bind(this);
            this.dismissMessage = this.dismissMessage.bind(this);
            this.isDirty = this.isDirty.bind(this);
            this.save = this.save.bind(this);
            this.serializeForm = this.serializeForm.bind(this);
            this.refForm = this.refForm.bind(this);

            this.$form = null;

            this.state = {
                error: null,
                invalidFields: new Set(),
                message: null,
                saving: false,
            };
        }

        onSubmit(event) {
            event.preventDefault();
            event.stopPropagation();

            // noinspection JSIgnoredPromiseFromCall
            this.save();
        }

        dismissMessage() {
            this.setState({ message: null });
        }

        isDirty() {
            if (this.$form) {
                return isDirty(this.$form);
            } else {
                return false;
            }
        }

        async save() {
            try {
                this.setState({
                    saving: true,
                    error: null,
                    invalidFields: new Set(),
                    message: null,
                });

                const result = await this.props.submit(this.serializeForm());

                this.setState({
                    saving: false,
                    message: {
                        title: `Успешно`,
                        type: `success`,
                        text: `Изменения сохранены`,
                    },
                });

                this.props.onSuccess(result);
            } catch (error) {
                if (error instanceof ValidationError) {
                    this.setState({
                        saving: false,
                        error,
                        invalidFields: error.fields,
                        message: {
                            title: `Не удалось сохранить изменения`,
                            type: `danger`,
                            text: error.flatErrors.join(`<br/>`),
                        },
                    });
                } else {
                    this.setState({
                        saving: false,
                        error,
                        message: {
                            title: `Не удалось сохранить изменения`,
                            type: `danger`,
                            text: error.toString(),
                        },
                    });
                }
                this.props.onError(error);
            }
        }

        // eslint-disable-next-line no-unused-vars
        serializeForm(ignoreFiles = false) {
            if (!this.$form || !this.$form.elements) {
                return new FormData();
            }

            const data = new FormData(this.$form);

            // Поскольку нельзя установить value у файлового инпута, приходится костылять вот таким образом
            [...this.$form.elements].forEach((el) => {
                //eslint-disable-next-line
                if (!el.hasOwnProperty(`getCustomValue`)) {
                    return;
                }

                // noinspection JSUnresolvedFunction
                const value = el.getCustomValue();

                if (ignoreFiles && value instanceof File) {
                    return;
                }

                // noinspection JSUnresolvedVariable
                data.set(
                    el.name,
                    value,
                    value instanceof File ? value.name : undefined
                );
            });

            return data;
        }

        refForm(el) {
            this.$form = el;
            if (isFunction(this.props.refForm)) {
                this.props.refForm(this.$form);
            }
        }

        render() {
            const { error, invalidFields, message, saving } = this.state;

            // eslint-disable-next-line no-unused-vars
            const { hasFiles, submit, onSuccess, refForm, id,  className, onChange, target,  action, ...props} = this.props;

            return (
                <form
                    ref={this.refForm}
                    encType={
                        hasFiles
                            ? `multipart/form-data`
                            : `application/x-www-form-urlencoded`
                    }
                    id={id}
                    onSubmit={this.onSubmit}
                    className={className}
                    onChange={onChange}
                >
                    <WrappedComponent
                        error={error}
                        invalidFields={invalidFields}
                        isDirty={this.isDirty}
                        message={message}
                        saving={saving}
                        dismissMessage={this.dismissMessage}
                        {...props}
                    />
                </form>
            );
        }
    }

    Form.displayName = `Form(${getDisplayName(WrappedComponent)})`;

    Form.propTypes = props;

    Form.defaultProps = {};

    return Form;
};

export default form;
export { props };
